// Section 7 and 8: planting, the harvest lag and the seed reserve. Farmers plant one plot each
// in Spring (0.5 seed per plot, from the reserve then from food); a planted plot is tended by
// the farmer who planted it and is lost if they stop farming; seed for next Spring is set aside
// at the Autumn harvest under the seed-reserve policy.
import { buildingsOf } from '../../core/queries';
import { sortedIds } from '../../core/sorted';
import type { PersonId, PhaseContext } from '../../core/types';
import { addStock } from '../shared/stocks';
import { num } from '../shared/value';

export function totalPlots(ctx: PhaseContext): number {
  let plots = 0;
  for (const type of Object.keys(
    ctx.registry.buildings,
  ) as (keyof typeof ctx.registry.buildings)[]) {
    const perBuilding = ctx.registry.buildings[type].plots;
    if (perBuilding > 0)
      plots += perBuilding * buildingsOf(ctx.draft, type).filter((b) => b.condition > 0).length;
  }
  return plots;
}

/** Spring: start a new planting year. */
export function startPlanting(ctx: PhaseContext): void {
  ctx.draft.plantedPlots = {};
  ctx.draft.fieldsPlanted = 0;
}

export interface PlantingTally {
  plots: number;
  seedUsed: number;
  fromFood: number;
  refused: number;
}

/** Plant for one farmer if a plot and seed are available. */
export function plant(
  ctx: PhaseContext,
  personId: PersonId,
  plots: number,
  tally: PlantingTally,
): void {
  const perPlot = ctx.cfg.jobs.farmer.seedPerPlot;
  for (let i = 0; i < plots; i++) {
    const free = totalPlots(ctx) - ctx.draft.fieldsPlanted;
    const seedOnHand = ctx.draft.seedReserved + ctx.draft.stocks.food.amount;
    if (free <= 0 || seedOnHand < perPlot) {
      tally.refused++;
      continue;
    }
    const fromSeed = Math.min(ctx.draft.seedReserved, perPlot);
    ctx.draft.seedReserved -= fromSeed;
    const fromFood = perPlot - fromSeed;
    if (fromFood > 0) addStock(ctx, 'food', -fromFood, 'other');
    ctx.draft.plantedPlots[personId] = (ctx.draft.plantedPlots[personId] ?? 0) + 1;
    ctx.draft.fieldsPlanted += 1;
    tally.plots++;
    tally.seedUsed += perPlot;
    tally.fromFood += fromFood;
  }
}

/** Summer and Autumn: plots whose planter no longer farms are lost (Section 8). */
export function losePlots(ctx: PhaseContext): void {
  for (const id of sortedIds(ctx.draft.plantedPlots)) {
    const p = ctx.draft.people[id];
    if (p?.job === 'farmer') continue;
    ctx.report.add({
      kind: 'warning',
      code: 'plot-lost',
      personId: id,
      amount: ctx.draft.plantedPlots[id] ?? 0,
    });
    delete ctx.draft.plantedPlots[id];
  }
}

/**
 * Autumn, after the harvest: clear the plots and set seed aside for next Spring. The seed spoils
 * as food (Section 7) through the seasons before planting, so the reserve is sized to still hold
 * next Spring's seed then (M7: `jobs.farmer.seedSpoilageSeasons` at this season's food
 * spoilage; without it a granary's 3% left the reserve one plot short every Spring).
 */
export function reserveSeed(ctx: PhaseContext): void {
  ctx.draft.plantedPlots = {};
  const policy = ctx.draft.policies.seedReserve;
  if (policy === 'eat-freely') return;
  const spoil = Math.min(0.9, Math.max(0, num(ctx, { kind: 'spoilage', resource: 'food' })));
  let kept = 1;
  for (let i = 0; i < ctx.cfg.jobs.farmer.seedSpoilageSeasons; i++) kept *= 1 - spoil;
  const target = (totalPlots(ctx) * ctx.cfg.jobs.farmer.seedPerPlot) / kept;
  const need = Math.max(0, target - ctx.draft.seedReserved);
  const moved = -addStock(ctx, 'food', -need, 'other');
  ctx.draft.seedReserved += moved;
  if (moved > 0) ctx.report.add({ kind: 'seed', action: 'reserved', amount: moved });
  if (moved < need)
    ctx.report.add({ kind: 'warning', code: 'seed-not-reserved', amount: need - moved });
}
